// Sidebar.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiMessageSquare,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiTrash2,
  FiPlus,
  FiX,
  FiEdit2,
  FiFolder,
  FiCopy,
} from "react-icons/fi";
import { HiDotsHorizontal } from "react-icons/hi";
import { SiChatbot } from "react-icons/si";
import { VscLayoutSidebarLeft, VscLayoutSidebarRight } from "react-icons/vsc";
import { useNavigate, useLocation } from "react-router-dom";
import ENDPOINTS from "../../api/endpoint";
import SearchModal from "../SearchModal";
import NewChatbotModal from "../NewChatbotModal";
import NewPromptFolderModal from "../NewPromptFolderModal";
import NewPromptModal from "../NewPromptModal";
import { showErrorToast, showSuccessToast } from "../Toast";

export default function Sidebar({
  onRequestDeleteConversation,
  refreshTrigger,
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [showHistory, setShowHistory] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [chatbots, setChatbots] = useState([]);
  const [isDeleteChatbotModalOpen, setIsDeleteChatbotModalOpen] =
    useState(false);
  const [chatbotToDelete, setChatbotToDelete] = useState(null);
  const [showChatbots, setShowChatbots] = useState(true);
  const [isChatbotModalOpen, setIsChatbotModalOpen] = useState(false);
  const [activeChatbotId, setActiveChatbotId] = useState(null);
  const [editingChatbotId, setEditingChatbotId] = useState(null);
  const [editingChatbotData, setEditingChatbotData] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [chatbotConversations, setChatbotConversations] = useState({});
  const [openChatbotMenuId, setOpenChatbotMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRefs = useRef({});

  // Prompt Library (folders + prompts)
  const [promptFolders, setPromptFolders] = useState([]);
  const [folderPrompts, setFolderPrompts] = useState({});
  const [showPromptLibrary, setShowPromptLibrary] = useState(true);
  const [isPromptFolderModalOpen, setIsPromptFolderModalOpen] = useState(false);
  const [editingPromptFolderId, setEditingPromptFolderId] = useState(null);
  const [editingPromptFolderName, setEditingPromptFolderName] = useState("");
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [selectedFolderIdForPrompt, setSelectedFolderIdForPrompt] = useState(null);
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [editingPromptData, setEditingPromptData] = useState(null);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [isDeletePromptModalOpen, setIsDeletePromptModalOpen] = useState(false);
  const [openFolderMenuId, setOpenFolderMenuId] = useState(null);
  const [folderMenuPosition, setFolderMenuPosition] = useState({ top: 0, right: 0 });
  const folderMenuButtonRefs = useRef({});
  const [openPromptMenuKey, setOpenPromptMenuKey] = useState(null); // "folderId_promptId"
  const [promptMenuPosition, setPromptMenuPosition] = useState({ top: 0, right: 0 });
  const promptMenuButtonRefs = useRef({});
  const [promptLibraryLoading, setPromptLibraryLoading] = useState(false);
  const [expandedPromptFolderId, setExpandedPromptFolderId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNewChat = () => {
    // Clear active conversation when starting new chat
    setActiveConversationId(null);
    localStorage.removeItem("conversation_id");
    // Clear active chatbot when navigating to /home
    setActiveChatbotId(null);
    localStorage.removeItem("active_chatbot_id");
    navigate("/home", { state: { refresh: Date.now() } });
  };

  // Function to get current conversation ID from localStorage
  const getCurrentConversationId = () => {
    return localStorage.getItem("conversation_id");
  };

  // Function to update active conversation (only on /home route)
  const updateActiveConversation = useCallback(() => {
    const currentPath = window.location.pathname;
    const isHomeRoute =
      currentPath.endsWith("/home") ||
      currentPath === "/home" ||
      currentPath.endsWith("/frontend/home");

    if (isHomeRoute) {
      const currentId = getCurrentConversationId();
      setActiveConversationId(currentId);
    } else {
      // Clear active conversation if not on /home
      setActiveConversationId(null);
    }
  }, []);

  // Extract fetchConversations so it can be called both from useEffect and imperatively
  const fetchConversations = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !userId) return;

    // Check if API_BASE_URL is configured
    if (!process.env.REACT_APP_API_BASE_URL) {
      console.error("REACT_APP_API_BASE_URL is not configured");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        ENDPOINTS.GET_CONVERSATION_List(companyId, userId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      const data = await res.json();
      if (!res.ok) {
        console.error(data.detail || "Failed to fetch conversations");
        return;
      }

      setConversations(data || []);
    } catch (err) {
      console.error("Error fetching conversations:", err);
      // Don't show error toast here as it might be called frequently
      // and user might not be logged in yet
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchConversations();
    // Initialize active conversation from localStorage
    updateActiveConversation();
  }, [updateActiveConversation]);

  // Fetch chatbots from API
  const fetchChatbots = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !token) return;

    try {
      const res = await fetch(ENDPOINTS.GET_CHATBOTS(companyId, userId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Failed to fetch chatbots");
        return;
      }

      const data = await res.json();

      // Map API response to component format
      const mappedChatbots = Array.isArray(data)
        ? data.map((bot) => ({
          id: bot.id,
          name: bot.name,
          namespace: bot.namespace,
          description: bot.description,
          llm_settings: bot.llm_settings || {},
          vector_settings: bot.vector_settings || {},
          normalization_dictionary: bot.normalization_dictionary || {},
          is_default: bot.is_default || false,
          is_active: bot.is_active || false,
          is_restricted: bot.is_restricted || false,
        }))
        : [];

      setChatbots(mappedChatbots);
    } catch (err) {
      console.error("Error fetching chatbots:", err);
    }
  }, []);

  // Helper function to fetch conversations for a chatbot
  const fetchChatbotConversations = useCallback(async (botId) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !userId || !token) {
      return;
    }

    try {
      const response = await fetch(
        ENDPOINTS.GET_CONVERSATIONS_BY_CHATBOT(companyId, userId, botId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log("Conversations filtered by chatbot:", data);
        // Store conversations for this chatbot
        setChatbotConversations((prev) => ({
          ...prev,
          [botId]: data || [],
        }));
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error fetching conversations by chatbot:", errorData);
      }
    } catch (err) {
      console.error("Error calling conversations by chatbot API:", err);
    }
  }, []);

  // Fetch prompt library folders
  const fetchPromptFolders = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const token = user?.access_token;

    if (!companyId || !token) return;

    try {
      const res = await fetch(ENDPOINTS.GET_PROMPT_FOLDERS(companyId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Failed to fetch prompt folders");
        return;
      }

      const data = await res.json();
      setPromptFolders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching prompt folders:", err);
    }
  }, []);

  // Fetch prompts for a folder
  const fetchFolderPrompts = useCallback(async (folderId) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const token = user?.access_token;

    if (!companyId || !token || !folderId) return;

    try {
      const res = await fetch(ENDPOINTS.GET_FOLDER_PROMPTS(companyId, folderId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setFolderPrompts((prev) => ({
          ...prev,
          [folderId]: Array.isArray(data) ? data : [],
        }));
      }
    } catch (err) {
      console.error("Error fetching folder prompts:", err);
    }
  }, []);

  // Load chatbots and prompt folders on component mount
  useEffect(() => {
    fetchChatbots();
    fetchPromptFolders();
    // Load active chatbot from localStorage
    const savedActiveChatbotId = localStorage.getItem("active_chatbot_id");
    if (savedActiveChatbotId) {
      setActiveChatbotId(savedActiveChatbotId);
    }
  }, [fetchChatbots, fetchPromptFolders]);

  // Fetch conversations when activeChatbotId changes
  useEffect(() => {
    if (activeChatbotId && !chatbotConversations[activeChatbotId]) {
      fetchChatbotConversations(activeChatbotId);
    }
  }, [activeChatbotId, fetchChatbotConversations, chatbotConversations]);

  // Fetch prompts for each folder when folders are loaded
  useEffect(() => {
    promptFolders.forEach((folder) => {
      if (folderPrompts[folder.id] === undefined) {
        fetchFolderPrompts(folder.id);
      }
    });
  }, [promptFolders, folderPrompts, fetchFolderPrompts]);

  // Update active conversation from localStorage when route changes to chatbot
  useEffect(() => {
    const currentPath = location.pathname;
    const pathMatch = currentPath.match(/\/chatbot\/([^/]+)/);

    if (pathMatch) {
      // On chatbot route, ensure conversation from localStorage is active
      const savedConversationId = localStorage.getItem("conversation_id");
      if (savedConversationId) {
        setActiveConversationId(savedConversationId);
      }
    }
  }, [location]);

  // Sync active chatbot and conversation with route
  useEffect(() => {
    const checkRoute = () => {
      const currentPath = window.location.pathname;
      const pathMatch = currentPath.match(/\/chatbot\/([^/]+)/);
      const isHomeRoute =
        currentPath.endsWith("/home") ||
        currentPath === "/home" ||
        currentPath.endsWith("/frontend/home");

      if (pathMatch) {
        // On /chatbot/:id route, set active chatbot
        const chatbotIdFromRoute = pathMatch[1];
        setActiveChatbotId(chatbotIdFromRoute);
        localStorage.setItem("active_chatbot_id", chatbotIdFromRoute);
        // Keep active conversation if it exists (it's set by handleConversationClick from chatbot conversations)
        const savedConversationId = localStorage.getItem("conversation_id");
        if (savedConversationId) {
          setActiveConversationId(savedConversationId);
        } else {
          // Only clear if no conversation is active
          setActiveConversationId(null);
        }
      } else if (isHomeRoute) {
        // On /home route, clear active chatbot
        setActiveChatbotId(null);
        localStorage.removeItem("active_chatbot_id");
        // Keep active conversation if it exists (it's set by handleConversationClick)
      } else {
        // On any other route, clear both active chatbot and conversation
        setActiveChatbotId(null);
        localStorage.removeItem("active_chatbot_id");
        setActiveConversationId(null);
        localStorage.removeItem("conversation_id");
      }
    };

    // Check on mount
    checkRoute();

    // Listen for route changes
    const handleRouteChange = () => {
      checkRoute();
    };

    window.addEventListener("popstate", handleRouteChange);

    // Also check periodically (fallback for programmatic navigation)
    const interval = setInterval(checkRoute, 100);

    return () => {
      window.removeEventListener("popstate", handleRouteChange);
      clearInterval(interval);
    };
  }, []);

  // Monitor conversation changes
  useEffect(() => {
    const handleStorageChange = () => {
      updateActiveConversation();
    };

    // Listen for storage changes (when conversation is changed from other tabs/components)
    window.addEventListener("storage", handleStorageChange);

    // Also check on focus (when user returns to tab)
    window.addEventListener("focus", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, [updateActiveConversation]);

  // Get user role from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsAdmin(user?.role === "admin");
  }, []);

  // Close dropdown menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInChatbotMenuContainer = event.target.closest(
        ".chatbot-menu-container"
      );
      const isClickInChatbotDropdown = event.target.closest(
        '[class*="z-[9999]"]'
      );
      const isClickInFolderMenuContainer = event.target.closest(
        ".prompt-folder-menu-container"
      );
      const isClickInFolderDropdown = event.target.closest(
        '[class*="z-[9998]"]'
      );
      const isClickInPromptMenuContainer = event.target.closest(
        ".prompt-item-menu-container"
      );
      const isClickInPromptDropdown = event.target.closest(
        '[class*="z-[9997]"]'
      );

      if (!isClickInChatbotMenuContainer && !isClickInChatbotDropdown) {
        setOpenChatbotMenuId(null);
      }
      if (!isClickInFolderMenuContainer && !isClickInFolderDropdown) {
        setOpenFolderMenuId(null);
      }
      if (!isClickInPromptMenuContainer && !isClickInPromptDropdown) {
        setOpenPromptMenuKey(null);
      }
    };

    if (openChatbotMenuId || openFolderMenuId || openPromptMenuKey) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openChatbotMenuId, openFolderMenuId, openPromptMenuKey]);

  // Re-fetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      fetchConversations();
    }
  }, [refreshTrigger]);

  // Expose refresh function globally
  useEffect(() => {
    window.refreshSidebarConversations = fetchConversations;

    window.refreshChatbotConversations = (chatbotId) => {
      if (chatbotId) {
        fetchChatbotConversations(chatbotId);
      } else if (activeChatbotId) {
        // If no chatbotId provided, use the active chatbot
        fetchChatbotConversations(activeChatbotId);
      }
    };

    window.removeConversationFromSidebar = (conversationId) => {
      setConversations((prevConversations) =>
        (prevConversations || []).filter((conv) => conv.id !== conversationId)
      );
    };

    window.updateSidebarActiveConversation = (conversationId) => {
      setActiveConversationId(conversationId);
    };

    return () => {
      delete window.refreshSidebarConversations;
      delete window.refreshChatbotConversations;
      delete window.removeConversationFromSidebar;
      delete window.updateSidebarActiveConversation;
    };
  }, [activeChatbotId, fetchChatbotConversations]);

  // Reset form function
  const resetChatbotForm = () => {
    setEditingChatbotId(null);
    setEditingChatbotData(null);
  };

  // Prompt folder handlers
  const resetPromptFolderForm = () => {
    setEditingPromptFolderId(null);
    setEditingPromptFolderName("");
  };

  const handleCreatePromptFolder = async (payload) => {
    const name = payload?.name?.trim();
    if (!name) {
      showErrorToast("Please enter a folder name");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    setPromptLibraryLoading(true);
    try {
      const isEditing = editingPromptFolderId !== null;
      const endpoint = isEditing
        ? ENDPOINTS.UPDATE_PROMPT_FOLDER(companyId, editingPromptFolderId)
        : ENDPOINTS.CREATE_PROMPT_FOLDER(companyId);
      const method = isEditing ? "PUT" : "POST";

      const res = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name }),
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(
          data.detail || data.message || `Failed to ${isEditing ? "update" : "create"} folder`
        );
        return;
      }

      showSuccessToast(`Folder ${isEditing ? "updated" : "created"} successfully`);
      resetPromptFolderForm();
      setIsPromptFolderModalOpen(false);
      await fetchPromptFolders();
    } catch (err) {
      console.error("Error with prompt folder:", err);
      showErrorToast(`Failed to ${editingPromptFolderId ? "update" : "create"} folder`);
    } finally {
      setPromptLibraryLoading(false);
    }
  };

  const handleEditPromptFolder = (folderId, e) => {
    e?.stopPropagation();
    const folder = promptFolders.find((f) => f.id === folderId);
    if (!folder) return;
    setEditingPromptFolderId(folderId);
    setEditingPromptFolderName(folder.name || "");
    setIsPromptFolderModalOpen(true);
  };

  const handleDeletePromptFolder = (folderId) => {
    const folder = promptFolders.find((f) => f.id === folderId);
    if (folder) {
      setFolderToDelete(folder);
      setIsDeleteFolderModalOpen(true);
    }
  };

  const confirmDeletePromptFolder = async () => {
    if (!folderToDelete) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    setPromptLibraryLoading(true);
    try {
      const res = await fetch(
        ENDPOINTS.DELETE_PROMPT_FOLDER(companyId, folderToDelete.id),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.detail || data.message || "Failed to delete folder");
        return;
      }

      showSuccessToast("Folder deleted successfully");
      setIsDeleteFolderModalOpen(false);
      setFolderToDelete(null);
      setFolderPrompts((prev) => {
        const next = { ...prev };
        delete next[folderToDelete.id];
        return next;
      });
      await fetchPromptFolders();
    } catch (err) {
      console.error("Error deleting folder:", err);
      showErrorToast("Failed to delete folder");
    } finally {
      setPromptLibraryLoading(false);
    }
  };

  // Prompt (inside folder) handlers
  const resetPromptForm = () => {
    setEditingPromptId(null);
    setEditingPromptData(null);
    setSelectedFolderIdForPrompt(null);
  };

  const openNewPromptModal = (folderId) => {
    setSelectedFolderIdForPrompt(folderId);
    setEditingPromptId(null);
    setEditingPromptData(null);
    setIsPromptModalOpen(true);
  };

  const handleCreatePrompt = async (payload) => {
    if (!selectedFolderIdForPrompt) return;

    const name = payload?.name?.trim();
    const template = payload?.template?.trim();
    if (!name || !template) {
      showErrorToast("Name and template are required");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) return;

    setPromptLibraryLoading(true);
    try {
      const isEditing = editingPromptId !== null;
      const endpoint = isEditing
        ? ENDPOINTS.UPDATE_FOLDER_PROMPT(
          companyId,
          selectedFolderIdForPrompt,
          editingPromptId
        )
        : ENDPOINTS.CREATE_FOLDER_PROMPT(companyId, selectedFolderIdForPrompt);
      const method = isEditing ? "PUT" : "POST";

      const body = isEditing
        ? { name, template: template || "", is_default: false }
        : {
          name,
          description: payload.description || "",
          template,
          is_default: false,
        };

      const res = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(
          data.detail || data.message || `Failed to ${isEditing ? "update" : "create"} prompt`
        );
        return;
      }

      showSuccessToast(`Prompt ${isEditing ? "updated" : "created"} successfully`);
      resetPromptForm();
      setIsPromptModalOpen(false);
      await fetchFolderPrompts(selectedFolderIdForPrompt);
    } catch (err) {
      console.error("Error with prompt:", err);
      showErrorToast(`Failed to ${editingPromptId ? "update" : "create"} prompt`);
    } finally {
      setPromptLibraryLoading(false);
    }
  };

  const handleEditPrompt = (folderId, promptId, e) => {
    e?.stopPropagation();
    const prompts = folderPrompts[folderId] || [];
    const prompt = prompts.find((p) => p.id === promptId);
    if (!prompt) return;
    setSelectedFolderIdForPrompt(folderId);
    setEditingPromptId(promptId);
    setEditingPromptData(prompt);
    setIsPromptModalOpen(true);
  };

  const handleDeletePrompt = (folderId, prompt) => {
    setPromptToDelete({ folderId, ...prompt });
    setIsDeletePromptModalOpen(true);
  };

  const handleCopyPromptTemplate = (prompt) => {
    const textToCopy = prompt?.template ?? "";

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => showSuccessToast("Template copied"))
        .catch(() => fallbackCopyTextToClipboard(textToCopy));
    } else {
      fallbackCopyTextToClipboard(textToCopy);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      if (successful) {
        showSuccessToast("Template copied");
      } else {
        showErrorToast("Failed to copy template");
      }
    } catch (err) {
      showErrorToast("Failed to copy template");
    } finally {
      document.body.removeChild(textArea);
    }
  };

  const confirmDeletePrompt = async () => {
    if (!promptToDelete) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) return;

    setPromptLibraryLoading(true);
    try {
      const res = await fetch(
        ENDPOINTS.DELETE_FOLDER_PROMPT(
          companyId,
          promptToDelete.folderId,
          promptToDelete.id
        ),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.detail || data.message || "Failed to delete prompt");
        return;
      }

      showSuccessToast("Prompt deleted successfully");
      setIsDeletePromptModalOpen(false);
      setFolderPrompts((prev) => ({
        ...prev,
        [promptToDelete.folderId]: (prev[promptToDelete.folderId] || []).filter(
          (p) => p.id !== promptToDelete.id
        ),
      }));
      setPromptToDelete(null);
    } catch (err) {
      console.error("Error deleting prompt:", err);
      showErrorToast("Failed to delete prompt");
    } finally {
      setPromptLibraryLoading(false);
    }
  };

  const handleCreateChatbot = async (chatbotData, accessSettings = null) => {
    const name = chatbotData.name.trim();
    if (!name) {
      showErrorToast("Please enter a chatbot name");
      return;
    }

    // Validate that name contains only alphabets, numbers, and spaces (no special characters)
    if (!/^[a-zA-Z0-9\s]+$/.test(name)) {
      showErrorToast(
        "Chatbot name can only contain letters, numbers, and spaces (no special characters)"
      );
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    setLoading(true);
    try {
      const isEditing = editingChatbotId !== null;
      const endpoint = isEditing
        ? ENDPOINTS.UPDATE_CHATBOT(companyId, editingChatbotId)
        : ENDPOINTS.CREATE_CHATBOT(companyId);
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(chatbotData),
      });

      const data = await response.json();

      if (!response.ok) {
        showErrorToast(
          data.detail ||
          data.message ||
          `Failed to ${isEditing ? "update" : "create"} chatbot`
        );
        return;
      }

      // Save user access settings (restricted chatbot user list)
      const chatbotIdForAccess = isEditing ? editingChatbotId : data.id;
      if (accessSettings && chatbotIdForAccess) {
        try {
          const accessRes = await fetch(
            ENDPOINTS.UPDATE_CHATBOT_ACCESS(companyId, chatbotIdForAccess),
            {
              method: "PUT",
              headers: {
                "Content-Type": "application/json",
                "X-User-Id": userId,
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(accessSettings),
            }
          );
          if (!accessRes.ok) {
            const accessErr = await accessRes.json().catch(() => ({}));
            showErrorToast(
              accessErr.detail ||
              "Chatbot saved, but updating user access failed"
            );
          }
        } catch (err) {
          showErrorToast("Chatbot saved, but updating user access failed");
        }
      }

      showSuccessToast(
        `Chatbot ${isEditing ? "updated" : "created"} successfully`
      );

      // Refresh chatbots list from API
      await fetchChatbots();

      // If creating a new chatbot, navigate to ChatbotMain page
      if (!isEditing && data.id) {
        setActiveChatbotId(data.id);
        localStorage.setItem("active_chatbot_id", data.id);
        navigate(`/chatbot/${data.id}`);
      }

      // Reset form and close modal
      resetChatbotForm();
      setIsChatbotModalOpen(false);
    } catch (err) {
      console.error(
        `Error ${editingChatbotId ? "updating" : "creating"} chatbot:`,
        err
      );
      showErrorToast(
        `Failed to ${editingChatbotId ? "update" : "create"
        } chatbot. Please try again`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChatbot = (botId) => {
    const chatbot = chatbots.find((bot) => bot.id === botId);
    if (chatbot) {
      setChatbotToDelete(chatbot);
      setIsDeleteChatbotModalOpen(true);
    }
  };

  // Confirm and delete chatbot
  const confirmDeleteChatbot = async () => {
    if (!chatbotToDelete) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    try {
      const response = await fetch(
        ENDPOINTS.DELETE_CHATBOT(companyId, chatbotToDelete.id),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        showErrorToast(
          data.detail || data.message || "Failed to delete chatbot"
        );
        return;
      }

      showSuccessToast("Chatbot deleted successfully");
      setIsDeleteChatbotModalOpen(false);
      setChatbotToDelete(null);

      // If the deleted chatbot was active, clear it
      if (activeChatbotId === chatbotToDelete.id) {
        setActiveChatbotId(null);
        localStorage.removeItem("active_chatbot_id");
        navigate("/home");
      }

      // Refresh chatbots list from API
      await fetchChatbots();

      // If on /doc, refresh documents (trigger event - Docs page listens)
      if (location.pathname.endsWith("/doc")) {
        window.dispatchEvent(new CustomEvent("refresh-documents"));
      }
    } catch (err) {
      console.error("Error deleting chatbot:", err);
      showErrorToast("Failed to delete chatbot. Please try again");
    }
  };

  const handleChatbotClick = async (botId) => {
    setActiveChatbotId(botId);
    localStorage.setItem("active_chatbot_id", botId);
    // Clear active conversation when navigating to chatbot page
    setActiveConversationId(null);
    localStorage.removeItem("conversation_id");

    // Fetch conversations for this chatbot before navigating
    await fetchChatbotConversations(botId);

    navigate(`/chatbot/${botId}`);
  };

  const handleEditChatbot = (botId, e) => {
    e?.stopPropagation(); // Prevent triggering chatbot click
    const bot = chatbots.find((b) => b.id === botId);
    if (!bot) return;

    // Set editing mode and chatbot data
    setEditingChatbotId(botId);
    setEditingChatbotData(bot);

    // Open modal
    setIsChatbotModalOpen(true);
  };

  const handleConversationClick = (conv, chatbotId = null) => {
    console.log("Clicked Conversation:", {
      id: conv.id,
      company_id: conv.company_id,
      user_id: conv.user_id,
      chatbot_id: chatbotId,
    });

    // Set active conversation
    setActiveConversationId(conv.id);
    localStorage.setItem("conversation_id", conv.id);

    if (chatbotId) {
      // If conversation is from a chatbot, keep that chatbot active
      setActiveChatbotId(chatbotId);
      localStorage.setItem("active_chatbot_id", chatbotId);

      // Navigate to chatbot page and load the conversation
      navigate(`/chatbot/${chatbotId}`, { state: { refresh: Date.now() } });

      // Load the conversation data after a short delay to ensure navigation completes
      setTimeout(() => {
        if (window.loadConversationData) {
          window.loadConversationData(conv.id);
        }
      }, 100);
    } else {
      // Clear active chatbot when clicking on history conversation (navigating to /home)
      setActiveChatbotId(null);
      localStorage.removeItem("active_chatbot_id");

      // Navigate to home and load the conversation
      navigate("/home", { state: { refresh: Date.now() } });

      // Load the conversation data after a short delay to ensure navigation completes
      setTimeout(() => {
        if (window.loadConversationData) {
          window.loadConversationData(conv.id);
        }
      }, 100);
    }
  };

  return (
    <div
      className={`dark:bg-bg-darkSecondary 
      transition-all duration-300 h-screen hidden md:flex flex-col
      ${isOpen ? "w-60" : "w-16"}`}
    >
      {/* Top Section */}
      <div className="flex items-center justify-between p-4">
        {isOpen && (
          <span className="text-indigo-900 dark:text-white font-bold text-lg"></span>
        )}
        <button onClick={() => setIsOpen(!isOpen)} className="py-2 rounded ">
          {isOpen ? (
            <VscLayoutSidebarLeft className="text-xl text-gray-800 dark:text-gray-200" />
          ) : (
            <VscLayoutSidebarRight className="text-xl text-gray-800 dark:text-gray-200" />
          )}
        </button>
      </div>

      {/* Navigation Links */}
      <nav className="px-2 space-y-2 md:mt-16">
        {/* {isAdmin && (
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-3 p-2 w-full text-left rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
          >
            <FiGrid className="text-lg" />
            {isOpen && <span>Dashboard</span>}
          </button>
        )} */}
        {/* New Chat */}
        <button
          onClick={handleNewChat}
          className="flex items-center gap-3 p-2 w-full text-left rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        >
          <FiMessageSquare className="text-lg" />
          {isOpen && <span>New Chat</span>}
        </button>

        {/* Search */}
        <button
          className="flex items-center gap-3 p-2 w-full text-left rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
          onClick={() => setIsSearchModalOpen(true)}
        >
          <FiSearch className="text-lg" />
          {isOpen && <span>Search</span>}
        </button>

        {/* History Link */}
        {/* <Link
          to="/history"
          className="flex items-center gap-3 p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
        >
          <FiClock className="text-lg" />
          {isOpen && <span>History</span>}
        </Link> */}
      </nav>
      {/* Prompt Library Section */}
      {isOpen && (
        <div className="px-2 mt-3 space-y-3">
          <button
            onClick={() => setShowPromptLibrary((prev) => !prev)}
            className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <span>Prompt Library</span>
            {showPromptLibrary ? (
              <FiChevronDown className="text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronUp className="text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {showPromptLibrary && (
            <div className="no-scrollbar max-h-[280px] overflow-y-auto">
              <ul className="space-y-1 pl-2">
                {isAdmin && (
                  <li>
                    <button
                      onClick={() => {
                        resetPromptFolderForm();
                        setIsPromptFolderModalOpen(true);
                      }}
                      className="w-full flex items-center gap-2 truncate text-sm p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                    >
                      <FiPlus className="text-base" />
                      <span className="truncate">New folder</span>
                    </button>
                  </li>
                )}

                {promptFolders.length === 0 ? (
                  <li className="text-xs text-gray-500 dark:text-gray-400 px-2 py-2">
                    No folders yet. Create one to add prompts.
                  </li>
                ) : (
                  promptFolders.map((folder) => {
                    const prompts = folderPrompts[folder.id] || [];
                    const isExpanded = expandedPromptFolderId === folder.id;
                    return (
                      <li key={folder.id} className="space-y-1">
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => setExpandedPromptFolderId((prev) => (prev === folder.id ? null : folder.id))}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setExpandedPromptFolderId((prev) => (prev === folder.id ? null : folder.id));
                            }
                          }}
                          className="flex items-center justify-between truncate text-sm p-2 rounded-md transition-colors cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                          title={folder.name}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <FiFolder className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
                            <span className="truncate">{folder.name}</span>
                            {/* {isExpanded ? (
                              <FiChevronUp className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
                            ) : (
                              <FiChevronDown className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
                            )} */}
                          </div>
                          {isAdmin && (
                            <div
                              className="prompt-folder-menu-container relative flex-shrink-0 ml-2"
                              ref={(el) => {
                                folderMenuButtonRefs.current[folder.id] = el;
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <HiDotsHorizontal
                                className="text-text-dark hover:text-gray-700 dark:text-gray-400 dark:hover:text-white cursor-pointer"
                                title="More options"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openFolderMenuId === folder.id) {
                                    setOpenFolderMenuId(null);
                                  } else {
                                    const buttonEl = folderMenuButtonRefs.current[folder.id];
                                    if (buttonEl) {
                                      const rect = buttonEl.getBoundingClientRect();
                                      setFolderMenuPosition({
                                        top: rect.bottom + 8,
                                        right: window.innerWidth - rect.right,
                                      });
                                    }
                                    setOpenFolderMenuId(folder.id);
                                  }
                                }}
                                aria-label="More options"
                              />
                            </div>
                          )}
                        </div>
                        {/* Prompts under folder - only when expanded */}
                        {isExpanded && (
                          <div className="ml-4 space-y-1 pl-2 border-l-2 border-gray-300 dark:border-gray-600">
                            {isAdmin && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openNewPromptModal(folder.id);
                                }}
                                className="w-full flex items-center gap-2 truncate text-xs p-1.5 rounded-md text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                              >
                                <FiPlus className="text-sm" />
                                <span>Create prompt</span>
                              </button>
                            )}
                            {prompts.length > 0 &&
                              prompts.map((prompt) => {
                                const menuKey = `${folder.id}_${prompt.id}`;
                                return (
                                  <li
                                    key={prompt.id}
                                    className="flex items-center justify-between truncate text-xs p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400 group"
                                    title={prompt.description || prompt.name}
                                  >
                                    <span className="truncate flex-1">{prompt.name}</span>
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <FiCopy
                                        className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer opacity-0 group-hover:opacity-100"
                                        title="Copy template"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleCopyPromptTemplate(prompt);
                                        }}
                                        aria-label="Copy template"
                                      />
                                      {isAdmin && (
                                        <>
                                          <div
                                            className="prompt-item-menu-container relative"
                                            ref={(el) => {
                                              promptMenuButtonRefs.current[menuKey] = el;
                                            }}
                                          >
                                            <HiDotsHorizontal
                                              className="text-gray-500 hover:text-gray-700 dark:hover:text-white cursor-pointer opacity-0 group-hover:opacity-100"
                                              title="More options"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (openPromptMenuKey === menuKey) {
                                                  setOpenPromptMenuKey(null);
                                                } else {
                                                  const buttonEl = promptMenuButtonRefs.current[menuKey];
                                                  if (buttonEl) {
                                                    const rect = buttonEl.getBoundingClientRect();
                                                    setPromptMenuPosition({
                                                      top: rect.bottom + 8,
                                                      right: window.innerWidth - rect.right,
                                                    });
                                                  }
                                                  setOpenPromptMenuKey(menuKey);
                                                }
                                              }}
                                              aria-label="More options"
                                            />
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </li>
                                );
                              })}
                          </div>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Chatbots Section */}
      {isOpen && (
        <div className="px-2 mt-3 space-y-3">
          <button
            onClick={() => setShowChatbots((prev) => !prev)}
            // className="flex w-full items-center justify-between text-sm font-semibold text-gray-700 dark:text-gray-300 hover:text-indigo-700 dark:hover:text-white transition-colors"
            className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <span>Chatbots</span>
            {showChatbots ? (
              <FiChevronDown className="text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronUp className="text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {showChatbots && (
            <div className="  no-scrollbar ">
              <ul className="space-y-1 pl-2">
                {isAdmin && (
                  <li>
                    <button
                      onClick={() => {
                        resetChatbotForm();
                        setIsChatbotModalOpen(true);
                      }}
                      className="w-full flex items-center gap-2 truncate text-sm p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                    >
                      <FiPlus className="text-base" />
                      <span className="truncate">New Chatbot</span>
                    </button>
                  </li>
                )}

                {chatbots.length === 0 ? (
                  <li className="text-xs text-gray-500 dark:text-gray-400 px-2 py-2">
                    No chatbots yet. Create one to get started.
                  </li>
                ) : (
                  chatbots.map((bot) => {
                    const isActive = activeChatbotId === bot.id;
                    const botConversations = chatbotConversations[bot.id] || [];
                    return (
                      <li key={bot.id} className="space-y-1">
                        <div
                          onClick={() => handleChatbotClick(bot.id)}
                          className={`flex items-center justify-between truncate text-sm p-2 rounded-md transition-colors cursor-pointer ${isActive
                            ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 border-l-4 border-indigo-500"
                            : "hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                            }`}
                          title={bot.name}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <SiChatbot
                              className={`flex-shrink-0 ${isActive
                                ? "text-indigo-600 dark:text-white"
                                : "text-text-dark dark:text-white"
                                }`}
                            />
                            <span className="truncate">{bot.name}</span>
                          </div>
                          {/* edit and delete buttons */}
                          {isAdmin && (
                            <div
                              className="chatbot-menu-container relative flex-shrink-0 ml-2"
                              ref={(el) => {
                                menuButtonRefs.current[bot.id] = el;
                              }}
                            >
                              <HiDotsHorizontal
                                className={`${isActive
                                  ? "text-indigo-700 hover:text-indigo-900 dark:text-indigo-200 dark:hover:text-indigo-100"
                                  : "text-text-dark hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                                  } cursor-pointer`}
                                title="More options"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openChatbotMenuId === bot.id) {
                                    setOpenChatbotMenuId(null);
                                  } else {
                                    const buttonEl =
                                      menuButtonRefs.current[bot.id];
                                    if (buttonEl) {
                                      const rect =
                                        buttonEl.getBoundingClientRect();
                                      setMenuPosition({
                                        top: rect.bottom + 8,
                                        right: window.innerWidth - rect.right,
                                      });
                                    }
                                    setOpenChatbotMenuId(bot.id);
                                  }
                                }}
                                aria-label="More options"
                              />
                            </div>
                          )}
                        </div>
                        {/* Conversations under chatbot */}
                        {botConversations.length > 0 && (
                          <ul className="ml-4 space-y-1 pl-2 border-l-2 border-gray-300 dark:border-gray-600">
                            {botConversations.map((conv) => {
                              // Check if conversation is active and we're on the chatbot route for this bot
                              const isOnChatbotRoute =
                                location.pathname.includes(
                                  `/chatbot/${bot.id}`
                                );
                              const isConvActive =
                                activeConversationId === conv.id &&
                                isOnChatbotRoute &&
                                activeChatbotId === bot.id;
                              return (
                                <li
                                  key={conv.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConversationClick(conv, bot.id);
                                  }}
                                  className={`flex items-center justify-between truncate text-xs p-1.5 rounded-md cursor-pointer transition-colors ${isConvActive
                                    ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100"
                                    : "hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
                                    }`}
                                  title={conv.title}
                                >
                                  <span className="truncate">{conv.title}</span>
                                  <FiTrash2
                                    className={`mr-2 flex-shrink-0 ${isConvActive
                                      ? "text-red-600 hover:text-red-800"
                                      : "text-red-500 hover:text-red-700"
                                      } cursor-pointer`}
                                    title="Delete conversation"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        typeof onRequestDeleteConversation ===
                                        "function"
                                      ) {
                                        onRequestDeleteConversation(conv);
                                        // Remove from chatbot conversations after deletion
                                        setChatbotConversations((prev) => ({
                                          ...prev,
                                          [bot.id]: (prev[bot.id] || []).filter(
                                            (c) => c.id !== conv.id
                                          ),
                                        }));
                                      }
                                    }}
                                    aria-label="Delete conversation"
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
        </div>
      )}



      {/* Scrollable History Section */}
      {isOpen && (
        <div className="px-2 pt-3 flex-1  no-scrollbar">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <span>General Chat</span>
            {showHistory ? (
              <FiChevronDown className="text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronUp className="text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {showHistory && (
            <ul className="mt-2 space-y-1 pl-2">
              {loading ? (
                <li className="text-sm text-gray-500 dark:text-gray-400">
                  Loading conversations...
                </li>
              ) : conversations.length > 0 ? (
                conversations.map((conv) => {
                  const isActive = activeConversationId === conv.id;
                  return (
                    <li
                      key={conv.id}
                      onClick={() => handleConversationClick(conv)}
                      className={`flex items-center justify-between truncate text-sm p-2 rounded-md cursor-pointer transition-colors ${isActive
                        ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 border-l-4 border-indigo-500"
                        : "hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                        }`}
                    >
                      <span className="truncate">{conv.title}</span>
                      <FiTrash2
                        className={`${isActive
                          ? "text-red-600 hover:text-red-800"
                          : "text-red-500 hover:text-red-700"
                          } cursor-pointer ml-2 flex-shrink-0`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            typeof onRequestDeleteConversation === "function"
                          ) {
                            onRequestDeleteConversation(conv);
                          }
                        }}
                      />
                    </li>
                  );
                })
              ) : (
                <li className="text-sm text-gray-500 dark:text-gray-400">
                  No conversations found.
                </li>
              )}
            </ul>
          )}
        </div>
      )}

      {/* Search Modal */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      />

      {/* Delete Chatbot Confirmation Modal */}
      {isDeleteChatbotModalOpen && chatbotToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                setIsDeleteChatbotModalOpen(false);
                setChatbotToDelete(null);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            >
              <FiX size={18} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Chatbot
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete the chatbot{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                "{chatbotToDelete.name}"
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteChatbotModalOpen(false);
                  setChatbotToDelete(null);
                }}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteChatbot}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Chatbot Modal */}
      <NewChatbotModal
        isOpen={isChatbotModalOpen}
        onClose={() => {
          setIsChatbotModalOpen(false);
          resetChatbotForm();
        }}
        editingChatbotId={editingChatbotId}
        chatbotData={editingChatbotData}
        onSubmit={handleCreateChatbot}
        loading={loading}
      />

      {/* New Prompt Folder Modal */}
      <NewPromptFolderModal
        isOpen={isPromptFolderModalOpen}
        onClose={() => {
          setIsPromptFolderModalOpen(false);
          resetPromptFolderForm();
        }}
        editingFolderId={editingPromptFolderId}
        folderName={editingPromptFolderName}
        onSubmit={handleCreatePromptFolder}
        loading={promptLibraryLoading}
      />

      {/* New / Edit Prompt Modal */}
      <NewPromptModal
        isOpen={isPromptModalOpen}
        onClose={() => {
          setIsPromptModalOpen(false);
          resetPromptForm();
        }}
        folderId={selectedFolderIdForPrompt}
        editingPromptId={editingPromptId}
        promptData={editingPromptData}
        onSubmit={handleCreatePrompt}
        loading={promptLibraryLoading}
      />

      {/* Delete Prompt Folder Confirmation Modal */}
      {isDeleteFolderModalOpen && folderToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                setIsDeleteFolderModalOpen(false);
                setFolderToDelete(null);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            >
              <FiX size={18} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete folder
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete the folder{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                "{folderToDelete.name}"
              </span>
              ? All prompts inside will be removed. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeleteFolderModalOpen(false);
                  setFolderToDelete(null);
                }}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePromptFolder}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Prompt Confirmation Modal */}
      {isDeletePromptModalOpen && promptToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                setIsDeletePromptModalOpen(false);
                setPromptToDelete(null);
              }}
              className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
            >
              <FiX size={18} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete prompt
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete the prompt{" "}
              <span className="font-semibold text-gray-900 dark:text-white">
                "{promptToDelete.name}"
              </span>
              ? This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setIsDeletePromptModalOpen(false);
                  setPromptToDelete(null);
                }}
                className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeletePrompt}
                className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dropdown Menu Portal */}
      {openChatbotMenuId &&
        createPortal(
          <div
            className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-md shadow-xl border border-gray-200 dark:border-gray-700 min-w-[120px] py-1"
            style={{
              top: `${menuPosition.top}px`,
              right: `${menuPosition.right}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              onClick={(e) => {
                e.stopPropagation();
                handleEditChatbot(openChatbotMenuId, e);
                setOpenChatbotMenuId(null);
              }}
            >
              <FiEdit2 className="text-base flex-shrink-0" />
              <span>Edit</span>
            </button>
            <button
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteChatbot(openChatbotMenuId);
                setOpenChatbotMenuId(null);
              }}
            >
              <FiTrash2 className="text-base flex-shrink-0" />
              <span>Delete</span>
            </button>
          </div>,
          document.body
        )}

      {/* Prompt Folder Dropdown Portal */}
      {openFolderMenuId &&
        createPortal(
          <div
            className="fixed z-[9998] bg-white dark:bg-gray-800 rounded-md shadow-xl border border-gray-200 dark:border-gray-700 min-w-[120px] py-1"
            style={{
              top: `${folderMenuPosition.top}px`,
              right: `${folderMenuPosition.right}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              onClick={(e) => {
                e.stopPropagation();
                handleEditPromptFolder(openFolderMenuId, e);
                setOpenFolderMenuId(null);
              }}
            >
              <FiEdit2 className="text-base flex-shrink-0" />
              <span>Edit</span>
            </button>
            <button
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
              onClick={(e) => {
                e.stopPropagation();
                handleDeletePromptFolder(openFolderMenuId);
                setOpenFolderMenuId(null);
              }}
            >
              <FiTrash2 className="text-base flex-shrink-0" />
              <span>Delete</span>
            </button>
          </div>,
          document.body
        )}

      {/* Prompt Item Dropdown Portal (Edit / Delete) */}
      {openPromptMenuKey &&
        (() => {
          const [folderId, promptId] = openPromptMenuKey.split("_");
          const prompt = (folderPrompts[folderId] || []).find((p) => p.id === promptId);
          if (!prompt) return null;
          return createPortal(
            <div
              className="fixed z-[9997] bg-white dark:bg-gray-800 rounded-md shadow-xl border border-gray-200 dark:border-gray-700 min-w-[120px] py-1"
              style={{
                top: `${promptMenuPosition.top}px`,
                right: `${promptMenuPosition.right}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditPrompt(folderId, promptId, e);
                  setOpenPromptMenuKey(null);
                }}
              >
                <FiEdit2 className="text-base flex-shrink-0" />
                <span>Edit</span>
              </button>
              <button
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePrompt(folderId, prompt);
                  setOpenPromptMenuKey(null);
                }}
              >
                <FiTrash2 className="text-base flex-shrink-0" />
                <span>Delete</span>
              </button>
            </div>,
            document.body
          );
        })()}

    </div>
  );
}
